import React, { useDeferredValue, useEffect, useState } from 'react';
import { Card, Modal, ModalBody, ModalContent } from '@heroui/react';
import { Link } from 'react-router-dom';
import { SALES_JOB_QUERY_PATH } from '../../constants/salesDashboard';
import { useSalesJobDetails } from '../../hooks/sales/useSalesJobDetails';
import { useSalesPlgMembers } from '../../hooks/sales/useSalesPlgMembers';
import { useSalesOrganizationJobs } from '../../hooks/sales/useSalesOrganizationJobs';
import { useSalesOrganizations } from '../../hooks/sales/useSalesOrganizations';
import { useUpdateSalesOrganizationCredits } from '../../hooks/sales/useUpdateSalesOrganizationCredits';
import { SalesDashboardOrganization } from '../../services/api/dashboardController';
import { TeamMember } from '../../services/api/teamController';
import WisedocLogo from '../common/WisedocLogo';
import SalesJobDetailsPanel from './SalesJobDetailsPanel';

const numberFormatter = new Intl.NumberFormat('en-US');
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatDate = (value: number | null | undefined) => {
  if (!value || !Number.isFinite(value)) {
    return 'Unknown';
  }

  try {
    return dateFormatter.format(new Date(value));
  } catch (_error) {
    return 'Unknown';
  }
};

const formatNumber = (value: number | null | undefined) =>
  numberFormatter.format(
    typeof value === 'number' && Number.isFinite(value) ? value : 0,
  );

const formatMetricLabel = ({
  value,
  isLoading,
  hasError,
}: {
  value: number;
  isLoading: boolean;
  hasError?: boolean;
}) => {
  if (hasError) {
    return '--';
  }

  if (isLoading) {
    return '...';
  }

  return formatNumber(value);
};

const getOrgStatusLabel = (organization: SalesDashboardOrganization) =>
  organization.isOnboarded ? 'Onboarded' : 'In Setup';

const getOrgStatusClasses = (organization: SalesDashboardOrganization) =>
  organization.isOnboarded
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';

const getJobQueryErrorMessage = (error: unknown) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error' in error.response.data &&
    typeof error.response.data.error === 'string'
  ) {
    return error.response.data.error;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return 'Unable to load this job’s details right now.';
};

const OrganizationsIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="3"
      width="7"
      height="7"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="14"
      y="3"
      width="7"
      height="7"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="3"
      y="14"
      width="7"
      height="7"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect
      x="14"
      y="14"
      width="7"
      height="7"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MembersIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="8.25033"
      cy="8.2474"
      r="1.83333"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M11.9163 13.7474C11.9163 14.7599 11.9163 15.5807 8.24967 15.5807C4.58301 15.5807 4.58301 14.7599 4.58301 13.7474C4.58301 12.7349 6.22463 11.9141 8.24967 11.9141C10.2747 11.9141 11.9163 12.7349 11.9163 13.7474Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M20.1663 10.9974C20.1663 14.4544 20.1663 16.1828 19.0924 17.2568C18.0185 18.3307 16.29 18.3307 12.833 18.3307H9.16634C5.70937 18.3307 3.98089 18.3307 2.90695 17.2568C1.83301 16.1828 1.83301 14.4544 1.83301 10.9974C1.83301 7.54043 1.83301 5.81195 2.90695 4.738C3.98089 3.66406 5.70937 3.66406 9.16634 3.66406H12.833C16.29 3.66406 18.0185 3.66406 19.0924 4.738C19.5229 5.16847 19.7808 5.70411 19.9353 6.41406"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M17.417 11H13.7503"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M17.417 8.25H12.8337"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M17.417 13.75H14.667"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const JobLookupIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M20 20L16.65 16.65"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const NAV_ITEMS = [
  {
    id: 'organizations',
    label: 'Organizations',
    icon: <OrganizationsIcon />,
  },
  {
    id: 'members',
    label: 'Members',
    icon: <MembersIcon />,
  },
] as const;

type DashboardView = (typeof NAV_ITEMS)[number]['id'];

const getMemberStatusClasses = (member: TeamMember) =>
  member.status === 'PENDING'
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-emerald-200 bg-emerald-50 text-emerald-700';

const getMemberRoleClasses = (member: TeamMember) => {
  switch (member.role) {
    case 'ROOT_USER':
      return 'border-violet-200 bg-violet-50 text-violet-700';
    case 'ADMIN':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    case 'SALES':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-600';
  }
};

const getMemberInitials = (member: TeamMember) => {
  const fromName = member.name
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  if (fromName) {
    return fromName;
  }

  return member.email.slice(0, 2).toUpperCase();
};

const salesNavButtonClass = (isActive: boolean) =>
  [
    'group relative flex h-[60px] w-[72px] flex-col items-center justify-center gap-[6px] rounded-[12px] border-none bg-transparent transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7DD3FC] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1F33]',
    isActive
      ? "bg-[rgba(56,189,248,0.16)] text-[#E0F2FE] before:absolute before:bottom-[10px] before:left-[-8px] before:top-[10px] before:w-[3px] before:rounded-r-[100px] before:bg-[#38BDF8] before:content-['']"
      : 'text-[#8AA4BE] hover:bg-[rgba(56,189,248,0.1)] hover:text-[#BAE6FD]',
  ].join(' ');

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 4L12 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SalesDashboardPage = () => {
  const {
    data: organizations = [],
    isLoading: isLoadingOrganizations,
    isError: hasOrganizationsError,
  } = useSalesOrganizations();
  const [activeView, setActiveView] = useState<DashboardView>('organizations');
  const [searchValue, setSearchValue] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedJobDisplayId, setSelectedJobDisplayId] = useState('');
  const [creditDraft, setCreditDraft] = useState('');
  const [creditError, setCreditError] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);
  const updateSalesOrganizationCreditsMutation =
    useUpdateSalesOrganizationCredits();

  const normalizedSearchValue = deferredSearchValue.trim().toLowerCase();
  const filteredOrganizations = organizations.filter((organization) => {
    if (!normalizedSearchValue) {
      return true;
    }

    return [
      organization.organisationName,
      organization.website,
      organization.industry,
      organization.orgUuid,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalizedSearchValue));
  });

  useEffect(() => {
    if (!filteredOrganizations.length) {
      if (selectedOrgId) {
        setSelectedOrgId('');
      }
      return;
    }

    const selectedStillVisible = filteredOrganizations.some(
      (organization) => organization.orgUuid === selectedOrgId,
    );

    if (!selectedStillVisible) {
      setSelectedOrgId(filteredOrganizations[0].orgUuid);
    }
  }, [filteredOrganizations, selectedOrgId]);

  const selectedOrganization =
    filteredOrganizations.find(
      (organization) => organization.orgUuid === selectedOrgId,
    ) || null;

  useEffect(() => {
    if (!selectedOrganization) {
      setCreditDraft('');
      setCreditError('');
      return;
    }

    setCreditDraft(String(selectedOrganization.credits ?? 0));
    setCreditError('');
  }, [selectedOrganization?.orgUuid, selectedOrganization?.credits]);

  const {
    data: jobs = [],
    isLoading: isLoadingJobs,
    isError: hasJobsError,
  } = useSalesOrganizationJobs(selectedOrganization?.orgUuid || '');
  const selectedJobSummary =
    jobs.find((job) => job.displayJobId === selectedJobDisplayId) || null;
  const {
    data: selectedJobDetails,
    isLoading: isLoadingSelectedJobDetails,
    isError: hasSelectedJobDetailsError,
    error: selectedJobDetailsError,
  } = useSalesJobDetails(selectedJobSummary?.displayJobId || '');

  const onboardedCount = organizations.filter(
    (organization) => organization.isOnboarded,
  ).length;
  const {
    data: plgMembers = [],
    isLoading: isLoadingPlgMembers,
    isError: hasPlgMembersError,
  } = useSalesPlgMembers();
  const activePlgMembers = plgMembers.filter(
    (member) => member.status === 'ACTIVE',
  );
  const pendingPlgMembers = plgMembers.filter(
    (member) => member.status === 'PENDING',
  );

  useEffect(() => {
    setSelectedJobDisplayId('');
  }, [selectedOrgId]);

  useEffect(() => {
    if (!jobs.length) {
      if (selectedJobDisplayId) {
        setSelectedJobDisplayId('');
      }
      return;
    }

    const selectedJobStillVisible = jobs.some(
      (job) => job.displayJobId === selectedJobDisplayId,
    );

    if (!selectedJobStillVisible && selectedJobDisplayId) {
      setSelectedJobDisplayId('');
    }
  }, [jobs, selectedJobDisplayId]);

  const closeJobDetailsModal = () => {
    setSelectedJobDisplayId('');
  };

  const handleCreditSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedOrganization) return;

    const normalizedCreditDraft = creditDraft.trim();
    const nextCredits = Number(normalizedCreditDraft);

    if (
      !normalizedCreditDraft ||
      !Number.isInteger(nextCredits) ||
      nextCredits < 0
    ) {
      setCreditError('Enter a non-negative whole number.');
      return;
    }

    setCreditError('');
    try {
      await updateSalesOrganizationCreditsMutation.mutateAsync({
        orgId: selectedOrganization.orgUuid,
        credits: nextCredits,
      });
    } catch (_error) {
      // Toast is handled by the mutation hook.
    }
  };

  return (
    <main className="flex min-h-screen bg-[#FAF8F5] text-[#0F172A]">
      <aside className="sticky top-0 z-[100] flex h-screen w-[96px] min-w-[96px] flex-shrink-0 flex-col items-center overflow-visible border-r border-white/5 bg-[linear-gradient(180deg,#0B1F33_0%,#102A43_100%)] px-0 py-[16px]">
        <WisedocLogo
          className="mb-[20px] mt-[2px] flex h-[36px] w-[36px] items-center justify-center"
          fill="#7DD3FC"
        />

        <nav
          aria-label="PLG dashboard navigation"
          className="flex flex-1 flex-col items-center gap-[6px] px-[10px]"
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveView(item.id)}
              className={salesNavButtonClass(activeView === item.id)}
              aria-label={item.label}
            >
              <span className="h-[20px] w-[20px]">{item.icon}</span>
              <span className="text-center text-[11px] font-medium leading-none text-current">
                {item.label}
              </span>
            </button>
          ))}
        </nav>

        <div className="mt-auto flex w-full flex-col items-center gap-[8px] px-[10px]">
          <Link
            to={SALES_JOB_QUERY_PATH}
            className={salesNavButtonClass(false)}
            aria-label="Job Query"
          >
            <span className="h-[20px] w-[20px]">
              <JobLookupIcon />
            </span>
            <span className="text-center text-[11px] font-medium leading-none text-current">
              Job Query
            </span>
          </Link>

          <div className="flex w-[72px] flex-col gap-2">
            <div className="rounded-[12px] bg-[rgba(255,255,255,0.06)] px-3 py-2 text-center">
              <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#8AA4BE]">
                Orgs
              </div>
              <div className="mt-1 text-[15px] font-semibold text-white">
                {formatNumber(organizations.length)}
              </div>
            </div>
            <div className="rounded-[12px] bg-[rgba(255,255,255,0.06)] px-3 py-2 text-center">
              <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#8AA4BE]">
                Members
              </div>
              <div className="mt-1 text-[15px] font-semibold text-white">
                {formatMetricLabel({
                  value: activePlgMembers.length,
                  isLoading: isLoadingPlgMembers,
                  hasError: hasPlgMembersError,
                })}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <section className="h-screen flex-1 overflow-y-auto bg-[#F6F8FC]">
        <div className="mx-auto flex min-h-full max-w-[1400px] flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8 xl:h-full xl:py-6">
          <section className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-[linear-gradient(180deg,#FFFFFF_0%,#F8FBFF_100%)] px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:px-6">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-[320px] bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.16),transparent_62%)]" />

            <div className="relative flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <p className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">
                    Sales Dashboard
                  </p>
                  <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                    Enterprise view
                  </span>
                </div>

                <div className="mt-3">
                  <h1 className="text-[22px] font-semibold tracking-[-0.03em] text-slate-900">
                    Enterprise Sales Overview
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    Track onboarded organizations and internal PLG access from
                    one place.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 sm:justify-end">
                <div className="min-w-[148px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.04)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Organizations
                  </div>
                  <div className="mt-1.5 text-xl font-semibold text-slate-900">
                    {formatNumber(organizations.length)}
                  </div>
                </div>
                <div className="min-w-[148px] rounded-2xl border border-blue-200 bg-[linear-gradient(180deg,#EFF6FF_0%,#DBEAFE_100%)] px-4 py-3 shadow-[0_10px_24px_rgba(59,130,246,0.14)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">
                    Onboarded
                  </div>
                  <div className="mt-1.5 text-xl font-semibold text-slate-900">
                    {formatNumber(onboardedCount)}
                  </div>
                </div>
                <div className="min-w-[148px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.04)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    PLG members
                  </div>
                  <div className="mt-1.5 text-xl font-semibold text-slate-900">
                    {formatMetricLabel({
                      value: activePlgMembers.length,
                      isLoading: isLoadingPlgMembers,
                      hasError: hasPlgMembersError,
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {activeView === 'organizations' ? (
            <section className="grid gap-6 xl:min-h-0 xl:flex-1 xl:grid-cols-[360px,minmax(0,1fr)]">
              <Card className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] xl:flex xl:h-full xl:flex-col xl:overflow-hidden">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      Organizations
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Click an org to load its job summary.
                    </p>
                  </div>
                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                    {formatNumber(filteredOrganizations.length)} visible
                  </div>
                </div>

                <div className="mt-4">
                  <label
                    htmlFor="sales-org-filter"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
                  >
                    Filter
                  </label>
                  <input
                    id="sales-org-filter"
                    type="text"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    placeholder="Search by org name, website, industry, or ID"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white"
                  />
                </div>

                <div className="mt-5 flex max-h-[720px] flex-col gap-3 overflow-y-auto pr-1 xl:min-h-0 xl:max-h-none xl:flex-1">
                  {isLoadingOrganizations ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                      Loading organizations...
                    </div>
                  ) : null}

                  {!isLoadingOrganizations && hasOrganizationsError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                      Unable to load organizations right now.
                    </div>
                  ) : null}

                  {!isLoadingOrganizations &&
                  !hasOrganizationsError &&
                  !filteredOrganizations.length ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                      No organizations match the current filter.
                    </div>
                  ) : null}

                  {filteredOrganizations.map((organization) => {
                    const isSelected = organization.orgUuid === selectedOrgId;

                    return (
                      <button
                        key={organization.orgUuid}
                        type="button"
                        onClick={() => setSelectedOrgId(organization.orgUuid)}
                        className={`rounded-[22px] border p-4 text-left transition ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-[0_12px_30px_rgba(37,99,235,0.14)]'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-base font-semibold text-slate-900">
                              {organization.organisationName}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {organization.orgUuid}
                            </div>
                          </div>
                          <span
                            className={`inline-flex min-h-[30px] min-w-[94px] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-[0.02em] leading-none sm:min-w-[102px] sm:px-3 sm:text-[11px] ${getOrgStatusClasses(
                              organization,
                            )}`}
                          >
                            {getOrgStatusLabel(organization)}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-600">
                          <div>
                            <div className="text-xs uppercase tracking-[0.14em] text-slate-400">
                              Onboarded
                            </div>
                            <div className="mt-1">
                              {formatDate(organization.createdAt)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-[0.14em] text-slate-400">
                              Members
                            </div>
                            <div className="mt-1">
                              {formatNumber(organization.memberCount)}
                            </div>
                          </div>
                        </div>

                        {organization.industry ? (
                          <div className="mt-4 text-sm text-slate-600">
                            {organization.industry}
                          </div>
                        ) : null}

                        {organization.website ? (
                          <div className="mt-2 truncate text-sm text-blue-600">
                            {organization.website}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </Card>

              <Card className="flex min-h-[560px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] xl:h-full">
                {selectedOrganization ? (
                  <div className="flex min-h-0 flex-1 flex-col">
                    <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
                      <div>
                        <div className="text-sm text-slate-500">
                          Selected org
                        </div>
                        <h2 className="mt-1 text-2xl font-semibold text-slate-900">
                          {selectedOrganization.organisationName}
                        </h2>
                        <div className="mt-2 flex flex-wrap gap-2 text-sm text-slate-500">
                          <span>{selectedOrganization.orgUuid}</span>
                          {selectedOrganization.website ? (
                            <span>{selectedOrganization.website}</span>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-2xl bg-slate-50 px-4 py-3 lg:min-w-[176px]">
                        <div className="text-xs uppercase tracking-[0.14em] text-slate-400">
                          Jobs in view
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {formatNumber(jobs.length)}
                        </div>
                      </div>

                      <form
                        onSubmit={handleCreditSave}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 lg:min-w-[260px]"
                      >
                        <label
                          htmlFor="sales-org-credits"
                          className="block text-xs uppercase tracking-[0.14em] text-slate-400"
                        >
                          Credits
                        </label>
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            id="sales-org-credits"
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            value={creditDraft}
                            onChange={(event) => {
                              setCreditDraft(event.target.value);
                              if (creditError) setCreditError('');
                            }}
                            className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-blue-400"
                          />
                          <button
                            type="submit"
                            disabled={
                              updateSalesOrganizationCreditsMutation.isPending
                            }
                            className="inline-flex h-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                          >
                            {updateSalesOrganizationCreditsMutation.isPending
                              ? 'Saving'
                              : 'Save'}
                          </button>
                        </div>
                        {creditError ? (
                          <div className="mt-2 text-xs font-medium text-rose-600">
                            {creditError}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-slate-500">
                            Used for applicant credits when no payment method is
                            on file.
                          </div>
                        )}
                      </form>
                    </div>

                    <div className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[22px] border border-slate-200">
                      <div className="border-b border-slate-200 bg-white px-5 py-3 text-sm text-slate-500">
                        Click a job row to load the same detailed view used in
                        the Job ID query page.
                      </div>

                      <div className="min-h-0 flex-1 overflow-auto">
                        <div className="min-w-[820px]">
                          <div className="sticky top-0 z-10 grid grid-cols-[minmax(280px,3fr)_minmax(116px,0.95fr)_minmax(210px,1.25fr)_88px] gap-3 bg-slate-50 px-5 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            <div className="min-w-0">Job</div>
                            <div className="min-w-0">Job ID</div>
                            <div className="min-w-0">Job link</div>
                            <div className="min-w-0">Applications</div>
                          </div>

                          {isLoadingJobs ? (
                            <div className="px-5 py-12 text-center text-sm text-slate-500">
                              Loading jobs...
                            </div>
                          ) : null}

                          {!isLoadingJobs && hasJobsError ? (
                            <div className="px-5 py-12 text-center text-sm text-rose-700">
                              Unable to load this organization’s jobs right now.
                            </div>
                          ) : null}

                          {!isLoadingJobs && !hasJobsError && !jobs.length ? (
                            <div className="px-5 py-12 text-center text-sm text-slate-500">
                              No job postings found for this organization.
                            </div>
                          ) : null}

                          {!isLoadingJobs && !hasJobsError
                            ? jobs.map((job, index) => (
                                <div
                                  key={job.jobId}
                                  className={`grid grid-cols-[minmax(280px,3fr)_minmax(116px,0.95fr)_minmax(210px,1.25fr)_88px] gap-3 px-5 py-4 text-sm text-slate-700 ${
                                    index === 0
                                      ? ''
                                      : 'border-t border-slate-200'
                                  } bg-white`}
                                >
                                  <div className="min-w-0">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSelectedJobDisplayId(
                                          job.displayJobId,
                                        )
                                      }
                                      className="block w-full truncate text-left text-[15px] font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-800 hover:decoration-blue-500"
                                    >
                                      {job.jobTitle}
                                    </button>
                                    <div className="mt-1 truncate text-xs text-slate-500">
                                      {job.status}
                                    </div>
                                  </div>
                                  <div className="min-w-0 truncate font-medium text-slate-900">
                                    {job.displayJobId}
                                  </div>
                                  <div className="min-w-0">
                                    <a
                                      href={job.jobLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                      className="block truncate text-blue-600 underline-offset-4 hover:underline"
                                    >
                                      {job.jobLink}
                                    </a>
                                  </div>
                                  <div className="min-w-0 font-semibold text-slate-900">
                                    {formatNumber(job.receivedApplications)}
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full min-h-[360px] items-center justify-center rounded-[22px] border border-dashed border-slate-200 bg-slate-50 text-center text-sm text-slate-500">
                    Select an organization from the list to load its job
                    summary.
                  </div>
                )}
              </Card>
            </section>
          ) : (
            <section className="xl:min-h-0 xl:flex-1">
              <Card className="flex min-h-[560px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] xl:h-full">
                <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-sm text-slate-500">Internal org</div>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-900">
                      PLG Members
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-2 text-sm text-slate-500">
                      <span>Accepted and pending access members</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-[0.14em] text-slate-400">
                        Active
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">
                        {formatMetricLabel({
                          value: activePlgMembers.length,
                          isLoading: isLoadingPlgMembers,
                          hasError: hasPlgMembersError,
                        })}
                      </div>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-[0.14em] text-slate-400">
                        Pending
                      </div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">
                        {formatMetricLabel({
                          value: pendingPlgMembers.length,
                          isLoading: isLoadingPlgMembers,
                          hasError: hasPlgMembersError,
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 min-h-0 flex-1 overflow-auto rounded-[22px] border border-slate-200">
                  <div className="sticky top-0 z-10 grid grid-cols-[minmax(260px,2.2fr)_minmax(140px,0.9fr)_minmax(120px,0.8fr)_minmax(220px,1.3fr)] gap-3 bg-slate-50 px-5 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    <div>Member</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div>Email</div>
                  </div>

                  {isLoadingPlgMembers ? (
                    <div className="px-5 py-12 text-center text-sm text-slate-500">
                      Loading PLG members...
                    </div>
                  ) : null}

                  {!isLoadingPlgMembers && hasPlgMembersError ? (
                    <div className="px-5 py-12 text-center text-sm text-rose-700">
                      Unable to load PLG members right now.
                    </div>
                  ) : null}

                  {!isLoadingPlgMembers &&
                  !hasPlgMembersError &&
                  !plgMembers.length ? (
                    <div className="px-5 py-12 text-center text-sm text-slate-500">
                      No PLG members found.
                    </div>
                  ) : null}

                  {!isLoadingPlgMembers && !hasPlgMembersError
                    ? plgMembers.map((member, index) => (
                        <div
                          key={member.id}
                          className={`grid min-w-[820px] grid-cols-[minmax(260px,2.2fr)_minmax(140px,0.9fr)_minmax(120px,0.8fr)_minmax(220px,1.3fr)] gap-3 px-5 py-4 text-sm text-slate-700 ${
                            index === 0 ? '' : 'border-t border-slate-200'
                          } bg-white`}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
                              {getMemberInitials(member)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900">
                                {member.name || member.email}
                              </div>
                              <div className="mt-1 truncate text-xs text-slate-500">
                                {member.workspaces.length
                                  ? `${member.workspaces.length} workspace assignment${
                                      member.workspaces.length === 1 ? '' : 's'
                                    }`
                                  : 'No workspace assignments'}
                              </div>
                            </div>
                          </div>
                          <div className="min-w-0">
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getMemberRoleClasses(
                                member,
                              )}`}
                            >
                              {member.role}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getMemberStatusClasses(
                                member,
                              )}`}
                            >
                              {member.status === 'PENDING'
                                ? 'Pending'
                                : 'Active'}
                            </span>
                          </div>
                          <div className="min-w-0 truncate text-slate-600">
                            {member.email}
                          </div>
                        </div>
                      ))
                    : null}
                </div>
              </Card>
            </section>
          )}
        </div>
      </section>

      <Modal
        isOpen={Boolean(selectedJobDisplayId)}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            closeJobDetailsModal();
          }
        }}
        placement="center"
        scrollBehavior="inside"
        hideCloseButton
        classNames={{
          base: 'mx-4 my-3 h-[calc(100vh-24px)] w-[calc(100vw-32px)] max-w-[1320px] overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.22)] sm:mx-8 sm:my-6 sm:h-auto sm:max-h-[calc(100vh-56px)]',
          wrapper: 'items-center overflow-y-auto px-0 py-3 sm:px-2 sm:py-6',
          backdrop: 'bg-slate-950/45 backdrop-blur-[4px]',
          body: 'gap-0 p-0',
        }}
      >
        <ModalContent>
          <ModalBody className="relative max-h-[inherit] overflow-hidden">
            <div className="pointer-events-none absolute right-4 top-4 z-30 sm:right-5 sm:top-5">
              <button
                type="button"
                onClick={closeJobDetailsModal}
                className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-[0_14px_28px_rgba(15,23,42,0.12)] backdrop-blur transition hover:border-slate-300 hover:bg-white hover:text-slate-900"
                aria-label="Close job details"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.08),transparent_42%),#F4F7FB] px-4 py-4 sm:px-6 sm:py-6">
              <div className="mx-auto w-full max-w-[1160px]">
                {selectedJobSummary && isLoadingSelectedJobDetails ? (
                  <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-12 text-center text-sm text-slate-500 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
                    Loading full job details...
                  </div>
                ) : null}

                {selectedJobSummary && hasSelectedJobDetailsError ? (
                  <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-6 text-sm text-rose-700">
                    {getJobQueryErrorMessage(selectedJobDetailsError)}
                  </div>
                ) : null}

                {selectedJobSummary &&
                !isLoadingSelectedJobDetails &&
                !hasSelectedJobDetailsError &&
                selectedJobDetails ? (
                  <SalesJobDetailsPanel
                    jobDetails={selectedJobDetails}
                    requestedDisplayJobId={selectedJobSummary.displayJobId}
                    headingLabel="Selected job"
                    timestampLabel="Dashboard timestamp"
                  />
                ) : null}
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </main>
  );
};

export default SalesDashboardPage;
